import React, { useCallback, useEffect, useRef, useState } from 'react';
import { wifi } from 'capacitor-jd-wifi';
import { Loading3QuartersOutlined } from '@ant-design/icons';
import { nanoid } from '@reduxjs/toolkit';
import { Input, QRCode } from 'antd';
import { Icon } from '@fuxi/eevee-icon';
import { ScrollViewRefAttrs } from '@/components/scroll-view';
import { Header, ScrollView } from '@/components';
import { ALL_PAGES } from '@/constant';
import { useNav } from '@/hooks';

import cx from './index.module.scss';

interface WifiInfo {
  capabilities: string;
  frequency: string;
  SSID: string;
}

type GetQRCodeTextType = {
  SSID: string;
  capabilities: string;
  password: string;
};

interface Props {}

const DeviceNetworkConfig: React.FC<Props> = () => {
  const nav = useNav();
  const [isScanOK, setIsScanOK] = useState(false);
  const [currentWifiInfo, setCurrentWifiInfo] = useState({ ssid: null });
  const [willConnectWifiInfo, setWillConnectWifiInfo] = useState<Partial<WifiInfo>>(null);
  const [qrCodeInfoText, setQrCodeInfo] = useState<string>(null);
  const scrollViewRef = useRef<ScrollViewRefAttrs>(null);
  const [inputPassword, setInputPassword] = useState('');
  const [wifiList, setWifiList] = useState<WifiInfo[]>([]);

  // wifi密码限制必须大于8位
  const isCanConnect = inputPassword.length >= 8;

  const init = useCallback(async () => {
    wifi
      .getSSID()
      .then(result => {
        setCurrentWifiInfo(result);
      })
      .catch(e => {
        console.error('获取当前wifi信息失败', e);
      });

    try {
      const result = await wifi.scan();

      console.log('scan result', result);

      if (result.scanResult?.length) {
        setWifiList(result.scanResult);
        scrollViewRef.current?.handleRefresh();
      }
    } finally {
      setIsScanOK(true);
    }
  }, []);

  const generateQRCodeText = useCallback((params: GetQRCodeTextType) => {
    const { capabilities, SSID, password } = params || {};
    return `WIFI:T:${capabilities};S:${SSID};P:${password};;`;
  }, []);

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={cx('device-network-config')}>
      <Header title="设备配网" backConfig={{ text: '返回', action: () => nav(ALL_PAGES.HELP_FEEDBACK) }} />
      <div className={cx('wrapper-box')}>
        {/* 生成的二维码 */}
        {qrCodeInfoText ? (
          <div className={cx('qr-code-box')}>
            <div className={cx('qr-code-inner-box')}>
              <div className={cx('header')}>
                <div className={cx('header-info')}>连接的网络</div>
                {willConnectWifiInfo.SSID && (
                  <div className={cx('connected-item')}>
                    <Icon name="success" className={cx('success-icon')} />
                    <div className={cx('current-wifi-name')}>{willConnectWifiInfo.SSID}</div>
                    <Icon name="lock" className={cx('lock-icon')} />
                    <Icon name="wifi" className={cx('wifi-icon')} />
                  </div>
                )}
              </div>
              <div className={cx('content')}>
                <div className={cx('code-wrapper')}>
                  <QRCode size={300} bordered={false} type="svg" value={qrCodeInfoText} />
                  {/* <div style={{ position: 'absolute', bottom: '100px' }}>{qrCodeInfoText}</div> */}
                </div>
                <div className={cx('code-tip')}>请将生成二维码对准机器人摄像头完成配网</div>
              </div>
            </div>
          </div>
        ) : (
          // 输入wifi密码
          <div className={cx('wifi-box')}>
            {willConnectWifiInfo?.SSID ? (
              <div className={cx('connnect-wifi-box')}>
                <div className={cx('header')}>
                  <div className={cx('cancel')} onClick={() => setWillConnectWifiInfo(null)}>
                    取消
                  </div>
                  <div
                    className={cx('connect-text', { disabled: !isCanConnect })}
                    onClick={() => {
                      isCanConnect &&
                        setQrCodeInfo(
                          generateQRCodeText({
                            SSID: willConnectWifiInfo?.SSID,
                            capabilities: willConnectWifiInfo.capabilities,
                            password: inputPassword,
                          })
                        );
                    }}
                  >
                    配对
                  </div>
                </div>
                <div className={cx('content')}>
                  <div className={cx('tips')}>
                    <span>请输入</span>
                    <span className={cx('highlight')}>“{willConnectWifiInfo.SSID}”</span>
                    <span>的密码</span>
                  </div>
                  <div className={cx('input-wrapper')}>
                    <div className={cx('pwd-text')}>密码</div>
                    <Input
                      allowClear
                      className={cx('input')}
                      value={inputPassword}
                      onChange={e => setInputPassword(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            ) : (
              // wifi列表信息
              <div className={cx('wifi-info')}>
                <div className={cx('header')}>
                  <div className={cx('header-info')}>连接的网络</div>
                  {currentWifiInfo.ssid && (
                    <div
                      className={cx('connected-item')}
                      onClick={() =>
                        setWillConnectWifiInfo(wifiList.find(item2 => item2.SSID === currentWifiInfo.ssid))
                      }
                    >
                      <Icon name="success" className={cx('success-icon')} />
                      <div className={cx('current-wifi-name')}>{currentWifiInfo.ssid}</div>
                      <Icon name="lock" className={cx('lock-icon')} />
                      <Icon name="wifi" className={cx('wifi-icon')} />
                    </div>
                  )}
                </div>
                <div className={cx('content')}>
                  <div className={cx('content-info')}>
                    <div>网络</div>
                    {!isScanOK && <Loading3QuartersOutlined spin className={cx('loading-icon')} />}
                  </div>
                  <ScrollView ref={scrollViewRef} scrollY scrollX={false} className={cx('wifi-list')}>
                    <div>
                      {wifiList
                        .filter(item => !!item.SSID)
                        .map(item => (
                          <div key={nanoid()} className={cx('wifi-item')} onClick={() => setWillConnectWifiInfo(item)}>
                            <div className={cx('name')}>{item.SSID}</div>
                            {item.capabilities && <Icon name="lock" className={cx('lock-icon')} />}
                            <Icon name="wifi" className={cx('wifi-icon')} />
                          </div>
                        ))}
                    </div>
                  </ScrollView>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DeviceNetworkConfig;
